import type { WorldManifest } from '../../framework/world/manifest';

/**
 * Mosslight world manifest. Asset paths are relative to the asset origin (VITE_ASSET_ORIGIN, or
 * this site's own /assets when unset). Interim music is generated by scripts/audio/render-tracks.ts;
 * the owner's MP3s replace these entries without code changes.
 */
export const MOSSLIGHT_MANIFEST: WorldManifest = {
  schemaVersion: 1,
  id: 'mosslight',
  version: '1.0.0',
  title: 'Mosslight',
  tagline: 'A sheltered woodland pocket around an old root.',
  description:
    'Moss, leaf litter and a shallow basin beneath an old root. A field mouse explores, a small rabbit rests in the hollow, a snail takes its time, and the weather leaves its traces long after the rain.',
  moods: ['woodland', 'quiet', 'mossy', 'rain'],
  devOnly: false,
  preview: {
    poster: 'worlds/mosslight/poster.jpg',
    alt: 'A mossy forest floor at mouse-eye height, an old root arching over leaf litter in soft afternoon light.',
    palette: ['#2b3a1e', '#5c4a2c', '#a8b46a', '#1b2216'],
  },
  // Scanned surface set (ADR 0006): CC0 photoscans, processed by scripts/assets/scanned.py.
  assets: [
    { id: 'scanned-ground-albedo', url: 'worlds/mosslight/scanned/ground_albedo.jpg', kind: 'texture', bytes: 1052753, license: 'cc0-1.0' },
    { id: 'scanned-ground-normal', url: 'worlds/mosslight/scanned/ground_normal.jpg', kind: 'texture', bytes: 3050001, license: 'cc0-1.0' },
    { id: 'scanned-ground-orh', url: 'worlds/mosslight/scanned/ground_orh.jpg', kind: 'texture', bytes: 1362353, license: 'cc0-1.0' },
    { id: 'scanned-bark-albedo', url: 'worlds/mosslight/scanned/bark_albedo.jpg', kind: 'texture', bytes: 612078, license: 'cc0-1.0' },
    { id: 'scanned-bark-normal', url: 'worlds/mosslight/scanned/bark_normal.jpg', kind: 'texture', bytes: 1636859, license: 'cc0-1.0' },
    { id: 'scanned-bark-orh', url: 'worlds/mosslight/scanned/bark_orh.jpg', kind: 'texture', bytes: 959291, license: 'cc0-1.0' },
    { id: 'scanned-rock-albedo', url: 'worlds/mosslight/scanned/rock_albedo.jpg', kind: 'texture', bytes: 358741, license: 'cc0-1.0' },
    { id: 'scanned-rock-normal', url: 'worlds/mosslight/scanned/rock_normal.jpg', kind: 'texture', bytes: 483267, license: 'cc0-1.0' },
    { id: 'scanned-rock-orh', url: 'worlds/mosslight/scanned/rock_orh.jpg', kind: 'texture', bytes: 418719, license: 'cc0-1.0' },
    { id: 'scanned-leaves-albedo', url: 'worlds/mosslight/scanned/leaves_albedo.webp', kind: 'texture', bytes: 131970, license: 'cc0-1.0' },
    { id: 'scanned-leaves-data', url: 'worlds/mosslight/scanned/leaves_data.jpg', kind: 'texture', bytes: 224667, license: 'cc0-1.0' },
  ],
  playlist: {
    id: 'mosslight-interim',
    title: 'Mosslight',
    tracks: [
      {
        id: 'under-the-old-root',
        title: 'Under the Old Root',
        artist: 'Glass Box (generated)',
        sources: [{ url: 'audio/mosslight/under-the-old-root.mp3', type: 'audio/mpeg' }],
        durationSec: 209,
        license: 'gbr-generated-music',
        source: 'Generated by scripts/audio/render-tracks.ts (seed 101).',
      },
      {
        id: 'lantern-moss',
        title: 'Lantern Moss',
        artist: 'Glass Box (generated)',
        sources: [{ url: 'audio/mosslight/lantern-moss.mp3', type: 'audio/mpeg' }],
        durationSec: 193,
        license: 'gbr-generated-music',
        source: 'Generated by scripts/audio/render-tracks.ts (seed 202).',
      },
      {
        id: 'rain-in-the-hollow',
        title: 'Rain in the Hollow',
        artist: 'Glass Box (generated)',
        sources: [{ url: 'audio/mosslight/rain-in-the-hollow.mp3', type: 'audio/mpeg' }],
        durationSec: 230,
        license: 'gbr-generated-music',
        source: 'Generated by scripts/audio/render-tracks.ts (seed 303).',
      },
      {
        id: 'owl-light',
        title: 'Owl Light',
        artist: 'Glass Box (generated)',
        sources: [{ url: 'audio/mosslight/owl-light.mp3', type: 'audio/mpeg' }],
        durationSec: 265,
        license: 'gbr-generated-music',
        source: 'Generated by scripts/audio/render-tracks.ts (seed 404).',
      },
    ],
  },
  controls: {
    timeModes: ['living', 'local', 'hold'],
    stages: ['dawn', 'morning', 'afternoon', 'golden', 'dusk', 'night'],
    weatherMoods: ['auto', 'clear', 'overcast', 'rain'],
    zoom: [0.86, 1.08],
  },
  quality: {
    battery: { targetFps: 30, maxPixelRatio: 1, detail: 'low' },
    balanced: { targetFps: 60, maxPixelRatio: 1.5, detail: 'medium' },
    high: { targetFps: 60, maxPixelRatio: 2, detail: 'high' },
  },
  residents: [
    { id: 'mouse', species: 'field mouse', role: 'main', count: [1, 2, 2] },
    { id: 'rabbit', species: 'woodland rabbit', role: 'main', count: [1, 1, 1] },
    { id: 'squirrel', species: 'red squirrel', role: 'main', count: [1, 1, 1] },
    { id: 'snail', species: 'grove snail', role: 'main', count: [1, 2, 3] },
    { id: 'isopod', species: 'pill bug', role: 'supporting', count: [3, 5, 7] },
    { id: 'spider', species: 'orb-weaving spider', role: 'supporting', count: [1, 1, 1] },
    { id: 'gnat', species: 'tiny flying insects', role: 'ambient', count: [2, 4, 6] },
    { id: 'firefly', species: 'firefly', role: 'ambient', count: [3, 5, 7] },
    { id: 'owl', species: 'owl (distant)', role: 'ambient', count: [1, 1, 1] },
  ],
  compatibility: { framework: '^1.0', renderer: 'webgl2' },
};
